import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AclPermissionDetail } from '@y42/acl/models';
import { createAclPermissionDetail } from '@y42/acl/util/create-acl-permission-detail';
import { connectState, InputObservable, trackByValue } from '@y42/angular/util';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { ToolbarShape } from '../../canvas-main-toolbar.model';
import { toolbarBackdropClass } from '../../constants';

@UntilDestroy()
@Component({
  selector: 'y42-insert-shape-button',
  templateUrl: './insert-shape-button.component.html',
  styleUrls: ['./insert-shape-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsertShapeButtonComponent {
  @Input()
  @InputObservable()
  active = false;
  active$!: Observable<boolean>;

  @Input()
  @InputObservable()
  isCanvasEditable = createAclPermissionDetail({ allowed: false });
  private readonly isCanvasEditable$!: Observable<AclPermissionDetail>;

  @Output() selectTool = new EventEmitter<ToolbarShape>();

  readonly state = connectState(this, {
    shape: combineLatest({ isActive: this.active$, activeTool: this.selectTool.pipe(startWith(null)) }).pipe(
      map(({ isActive, activeTool }) => {
        if (isActive && activeTool) {
          return activeTool;
        }
        return shapes[0] as ToolbarShape;
      }),
    ),
    isCanvasEditable: this.isCanvasEditable$,
  });

  protected readonly firstShapes = shapes.slice(0, 9);
  protected readonly restShapes = shapes.slice(9);
  protected readonly toolbarBackdropClass = toolbarBackdropClass;

  protected showAllShapes = false;
  protected trackByValue = trackByValue;
}

const shapes: ToolbarShape[] = [
  {
    name: 'rectangle',
    svg: 'M 512 512 H 0 V 0 h 512 v 512 Z',
    icon: ['fal', 'square'],
  },
  {
    name: 'roundedRectangle',
    svg: 'M 400 0 H 48 C 21.5 0 0 21.5 0 48 v 352 c 0 26.5 21.5 48 48 48 h 352 c 26.5 0 48 -21.5 48 -48 V 48 c 0 -26.5 -21.5 -48 -48 -48 z',
    icon: ['fal', 'rectangle'],
  },
  {
    name: 'circle',
    svg: 'M 248 0 C 111 0 0 111 0 248 s 111 248 248 248 s 248 -111 248 -248 S 385 0 248 0 z',
    icon: ['fal', 'circle'],
  },
  {
    name: 'triangle',
    svg: 'M329.6 24c-18.4-32-64.7-32-83.2 0L6.5 440c-18.4 31.9 4.6 72 41.6 72H528c36.9 0 60-40 41.6-72l-240-416z',
    icon: ['fal', 'triangle'],
  },
  {
    name: 'diamond',
    svg: 'M242.2 8.3c-9.6-11.1-26.8-11.1-36.4 0l-200 232c-7.8 9-7.8 22.3 0 31.3l200 232c9.6 11.1 26.8 11.1 36.4 0l200-232c7.8-9 7.8-22.3 0-31.3l-200-232z',
    icon: ['fal', 'diamond'],
  },
  {
    name: 'hexagon',
    svg: 'M 425.5 23.8 C 416.9 9.1 401.1 0 384 0 H 160 c -17.1 0 -32.9 9.1 -41.5 23.8 l -112 192 c -8.7 14.9 -8.7 33.4 0 48.4 l 112 192 c 8.6 14.7 24.4 23.8 41.5 23.8 h 224 c 17.1 0 32.9 -9.1 41.5 -23.8 l 112 -192 c 8.7 -14.9 8.7 -33.4 0 -48.4 l -112 -192 z',
    icon: ['fal', 'hexagon'],
  },
  {
    name: 'comment',
    svg: 'M 256 0 C 397.4 0 512 93.1 512 208 c 0 49.6 -21.4 95 -57 130.7 C 467.5 389.1 509.3 434 509.8 434.5 c 2.2 2.3 2.8 5.7 1.5 8.7 S 507.2 448 504 448 c -66.3 0 -116 -31.8 -140.6 -51.4 c -32.7 12.3 -69 19.4 -107.4 19.4 c -141.4 0 -256 -93.1 -256 -208 S 114.6 0 256 0 z',
    icon: ['fal', 'comment'],
  },
  {
    name: 'star',
    svg: 'M 240.3 17.8 L 175 150.2 L 28.9 171.5 c -26.2 3.8 -36.7 36.1 -17.7 54.6 l 105.7 103 l -25 145.5 c -4.5 26.3 23.2 46 46.4 33.7 L 269 439.6 l 130.7 68.7 c 23.2 12.2 50.9 -7.4 46.4 -33.7 l -25 -145.5 l 105.7 -103 c 19 -18.5 8.5 -50.8 -17.7 -54.6 L 363 150.2 L 297.7 17.8 c -11.7 -23.6 -45.6 -23.9 -57.4 0 z',
    icon: ['fal', 'star'],
  },
  {
    name: 'arrowRight',
    svg: 'M 0 248 v -96 c 0 -13.3 10.7 -24 24 -24 h 200 V 24.2 c 0 -21.4 25.8 -32.1 41 -17 L 441 183 c 9.4 9.4 9.4 24.6 0 34 L 265 392.7 c -15.1 15.1 -41 4.4 -41 -17 V 272 H 24 c -13.3 0 -24 -10.7 -24 -24 z',
    icon: ['fal', 'right'],
  },
  {
    name: 'octagon',
    svg: 'M361.5 14.1c-9-9-21.2-14.1-33.9-14.1H184.5c-12.7 0-24.9 5.1-33.9 14.1L14.1 150.5c-9 9-14.1 21.2-14.1 33.9v143.1c0 12.7 5.1 24.9 14.1 33.9l136.5 136.5c9 9 21.2 14.1 33.9 14.1h143.1c12.7 0 24.9-5.1 33.9-14.1L498 361.4c9-9 14.1-21.2 14.1-33.9v-143c0-12.7-5.1-24.9-14.1-33.9L361.5 14.1z',
    icon: ['fal', 'octagon'],
  },
  {
    name: 'bookmark',
    svg: 'M0 512V48C0 21.49 21.49 0 48 0h288c26.51 0 48 21.49 48 48v464L192 400 0 512z',
    icon: ['fal', 'bookmark'],
  },
  {
    name: 'cloud',
    svg: 'M 537.6 194.6 c 4.1 -10.7 6.4 -22.4 6.4 -34.6 c 0 -53 -43 -96 -96 -96 c -19.7 0 -38.1 6 -53.3 16.2 C 367 32.2 315.3 0 256 0 c -88.4 0 -160 71.6 -160 160 c 0 2.7 0.1 5.4 0.2 8.1 C 40.2 187.8 0 241.2 0 304 c 0 79.5 64.5 144 144 144 h 368 c 70.7 0 128 -57.3 128 -128 c 0 -61.9 -44 -113.6 -102.4 -125.4 z',
    icon: ['fal', 'cloud'],
  },
  {
    name: 'file',
    svg: 'M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm160-14.1v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z',
    icon: ['fal', 'file'],
  },
  {
    name: 'heart',
    svg: 'M 462.3 30.6 C 407.5 -16.1 326 -7.7 275.7 44.2 L 256 64.5 l -19.7 -20.3 C 186.1 -7.7 104.5 -16.1 49.7 30.6 c -62.8 53.6 -66.1 149.8 -9.9 207.9 l 193.5 199.8 c 12.5 12.9 32.8 12.9 45.3 0 l 193.5 -199.8 c 56.3 -58.1 53 -154.3 -9.8 -207.9 z',
    icon: ['fal', 'heart'],
  },
  {
    name: 'hand',
    svg: 'M 512 135.652 c 0 23.625 -20.65 43.826 -44.8 43.826 h -99.851 c 16.34 17.048 18.346 49.766 -6.299 70.944 c 14.288 22.829 2.147 53.017 -16.45 62.315 C 353.574 361.878 322.654 384 272 384 c -2.746 0 -13.276 -0.203 -16 -0.195 c -61.971 0.168 -76.894 -31.065 -123.731 -38.315 C 120.596 343.683 112 333.599 112 321.786 V 150.261 l 0.002 -0.001 c 0.011 -18.366 10.607 -35.889 28.464 -43.845 c 28.886 -12.994 95.413 -49.038 107.534 -77.323 c 7.797 -18.194 21.384 -29.084 40 -29.092 c 34.222 -0.014 57.752 35.098 44.119 66.908 c -3.583 8.359 -8.312 16.67 -14.153 24.918 H 467.2 c 23.45 0 44.8 20.543 44.8 43.826 z M 96 136 v 192 c 0 13.255 -10.745 24 -24 24 H 24 c -13.255 0 -24 -10.745 -24 -24 V 136 c 0 -13.255 10.745 -24 24 -24 h 48 c 13.255 0 24 10.745 24 24 z M 68 304 c 0 -11.046 -8.954 -20 -20 -20 s -20 8.954 -20 20 s 8.954 20 20 20 s 20 -8.954 20 -20 z',
    icon: ['fal', 'hand-back-point-right'],
  },
  {
    name: 'bell',
    svg: 'M 224 512 C 259.32 512 287.97 483.35 287.97 448 H 160.03 C 160.03 483.35 188.68 512 224 512 Z M 439.39 362.29 C 420.07 341.53 383.92 310.3 383.92 208 C 383.92 130.3 329.44 68.1 255.98 52.84 V 32 C 255.98 14.33 241.66 0 224 0 S 192.02 14.33 192.02 32 V 52.84 C 118.56 68.1 64.08 130.3 64.08 208 C 64.08 310.3 27.93 341.53 8.61 362.29 C 2.61 368.74 -0.05 376.45 0 384 C 0.11 400.4 12.98 416 32.1 416 H 415.9 C 435.02 416 447.9 400.4 448 384 C 448.05 376.45 445.39 368.73 439.39 362.29 Z',
    icon: ['fal', 'bell'],
  },
  {
    name: 'database',
    svg: 'M448 73.143v45.714C448 159.143 347.667 192 224 192S0 159.143 0 118.857V73.143C0 32.857 100.333 0 224 0s224 32.857 224 73.143zM448 176v102.857C448 319.143 347.667 352 224 352S0 319.143 0 278.857V176c48.125 33.143 136.208 48.572 224 48.572S399.874 209.143 448 176zm0 160v102.857C448 479.143 347.667 512 224 512S0 479.143 0 438.857V336c48.125 33.143 136.208 48.572 224 48.572S399.874 369.143 448 336z',
    icon: ['fal', 'database'],
  },
  {
    name: 'bolt',
    svg: 'M296 160H180.6l42.6-129.8C227.2 15 215.7 0 200 0H56C44 0 33.8 8.9 32.2 20.8l-32 240C-1.7 275.2 9.5 288 24 288h118.7L96.6 482.5c-3.6 15.2 8 29.5 23.3 29.5 8.4 0 16.4-4.4 20.8-12l176-304c9.3-15.9-2.2-36-20.7-36z',
    icon: ['fal', 'bolt'],
  },
];
